import React from 'react';
import {Button} from '@material-ui/core';
import {fade, makeStyles} from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';
import PeopleIcon from '@material-ui/icons/People';

const SKELETON_ROWS = [
    [64, 128, 76, [96, 84], 150, 104],
    [48, 104, 64, [110], 128, 96],
    [56, 140, 64, [110], 20, 104],
    [40, 112, 84, [110], 160, 20],
    [64, 120, 76, [96, 84], 140, 104],
    [48, 132, 64, [110], 132, 96],
    [56, 96, 84, [96], 20, 104],
    [40, 124, 64, [110, 84], 148, 96]
];

const useStyles = makeStyles((theme) => {
    const isDark = theme.palette.type === 'dark';
    const line = fade(theme.palette.text.primary, 0.12);
    const line2 = fade(theme.palette.text.primary, isDark ? 0.08 : 0.07);
    return {
        card: {
            borderRadius: 16,
            border: `1px solid ${line2}`,
            boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.04)',
            backgroundColor: theme.palette.background.paper,
            overflow: 'hidden'
        },
        head: {
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            height: 44,
            padding: '0 16px',
            boxSizing: 'border-box',
            backgroundColor: fade(theme.palette.text.primary, isDark ? 0.03 : 0.02),
            borderBottom: `1px solid ${line}`
        },
        row: {
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            height: 48,
            padding: '0 16px',
            boxSizing: 'border-box',
            borderBottom: `1px solid ${line2}`,
            '&:last-child': {borderBottom: 0}
        },
        col: {
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flex: 'none'
        },
        colLogin: {width: 104},
        colName: {width: 224},
        colRole: {width: 104},
        colGroup: {flex: 1, minWidth: 0, gap: 4},
        colEmail: {width: 184},
        colContact: {width: 114},
        colBranch: {width: 74},
        bar: {
            borderRadius: 6
        },
        pill: {
            borderRadius: 11
        },
        empty: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8
        },
        emptyIcon: {
            width: 48,
            height: 48,
            borderRadius: 14,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: fade(theme.palette.text.primary, 0.05),
            color: theme.palette.text.disabled,
            '& svg': {fontSize: 26}
        },
        emptyTitle: {
            display: 'block',
            marginTop: 6,
            fontSize: 15,
            fontWeight: 600,
            lineHeight: 1.4,
            color: theme.palette.text.primary
        },
        emptyButton: {
            marginTop: 10,
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 600
        }
    };
});

export function UserTableSkeleton({columns}) {
    const classes = useStyles();
    return (
        <div className={classes.card} role="status" aria-busy="true">
            <div className={classes.head}>
                {columns.map((col) => (
                    <div key={col.name} className={`${classes.col} ${classes[col.widthClass]}`}>
                        <Skeleton animation="wave" variant="rect" width={col.headWidth} height={10} className={classes.bar}/>
                    </div>
                ))}
            </div>
            {SKELETON_ROWS.map((widths, idx) => (
                <div key={idx} className={classes.row}>
                    <div className={`${classes.col} ${classes.colLogin}`}>
                        <Skeleton animation="wave" variant="rect" width={widths[0]} height={12} className={classes.bar}/>
                    </div>
                    <div className={`${classes.col} ${classes.colName}`}>
                        <Skeleton animation="wave" variant="rect" width={widths[1]} height={12} className={classes.bar}/>
                    </div>
                    <div className={`${classes.col} ${classes.colRole}`}>
                        <Skeleton animation="wave" variant="rect" width={widths[2]} height={22} className={classes.pill}/>
                    </div>
                    <div className={`${classes.col} ${classes.colGroup}`}>
                        {widths[3].map((w, i) => (
                            <Skeleton key={i} animation="wave" variant="rect" width={w} height={22} className={classes.pill}/>
                        ))}
                    </div>
                    <div className={`${classes.col} ${classes.colEmail}`}>
                        <Skeleton animation="wave" variant="rect" width={widths[4]} height={12} className={classes.bar}/>
                    </div>
                    <div className={`${classes.col} ${classes.colContact}`}>
                        <Skeleton animation="wave" variant="rect" width={widths[5]} height={12} className={classes.bar}/>
                    </div>
                    <div className={`${classes.col} ${classes.colBranch}`}>
                        <Skeleton animation="wave" variant="rect" width={30} height={20} className={classes.bar}/>
                    </div>
                </div>
            ))}
        </div>
    );
}

export function UserTableEmpty({language, showClear, onClear}) {
    const classes = useStyles();
    return (
        <span className={classes.empty}>
            <span className={classes.emptyIcon}><PeopleIcon/></span>
            <span className={classes.emptyTitle}>{language.no_data}</span>
            {showClear && (
                <Button variant="outlined" size="small" className={classes.emptyButton} onClick={onClear}>
                    {language.clear}
                </Button>
            )}
        </span>
    );
}

export const SKELETON_COLUMNS = [
    {name: 'login', widthClass: 'colLogin', headWidth: 44},
    {name: 'name', widthClass: 'colName', headWidth: 44},
    {name: 'role', widthClass: 'colRole', headWidth: 36},
    {name: 'group', widthClass: 'colGroup', headWidth: 44},
    {name: 'email', widthClass: 'colEmail', headWidth: 40},
    {name: 'contact', widthClass: 'colContact', headWidth: 80},
    {name: 'branch', widthClass: 'colBranch', headWidth: 50}
];
